/**
 * RFC 5322 from-domain filter keys exposed by GET /backoffice/accounts (AO-1622).
 * Each key maps to an exclusive pair: setting one toggle to true forces the
 * partner to false (and vice versa) because there is only one value per key.
 */
export const FROM_DOMAINS_FILTER_KEYS = [
  'from-domains-exist',
  'from-domains-popup',
  'from-domains-confirmed',
  'from-domains-validation',
] as const;

export type FromDomainsFilterKey = typeof FROM_DOMAINS_FILTER_KEYS[number];

export interface FromDomainsFilterPair {
  key: FromDomainsFilterKey;
  positiveLabel: string;
  negativeLabel: string;
}

export const FROM_DOMAINS_FILTER_PAIRS: FromDomainsFilterPair[] = [
  {
    key: 'from-domains-exist',
    positiveLabel: 'At least one',
    negativeLabel: 'None',
  },
  {
    key: 'from-domains-popup',
    positiveLabel: 'Popup shown',
    negativeLabel: 'Popup not shown',
  },
  {
    key: 'from-domains-confirmed',
    positiveLabel: 'Confirmed',
    negativeLabel: 'Not confirmed',
  },
  {
    key: 'from-domains-validation',
    positiveLabel: 'Validation enabled',
    negativeLabel: 'Validation disabled',
  },
];

export type FromDomainsFilterState = Partial<
  Record<FromDomainsFilterKey, boolean>
>;

export interface FromDomainsFilterChange {
  key: FromDomainsFilterKey;
  value: boolean;
}

/**
 * License-based filters (popup, validation) are pushed down to MySQL and respect
 * pagination natively. PD-based filters (exist, confirmed) are applied as a
 * post-filter in BO Gateway after the paginated fetch, which means a 50-row page
 * can be reduced to almost nothing. Detect that case so the UI can request
 * load-all to keep the result complete.
 */
export function hasPrivateDomainFilter(state: FromDomainsFilterState): boolean {
  return (
    typeof state['from-domains-exist'] === 'boolean' ||
    typeof state['from-domains-confirmed'] === 'boolean'
  );
}

export function parseFromDomainsFilterParam(raw: unknown): boolean | undefined {
  if (raw === 'true' || raw === true) {
    return true;
  }
  if (raw === 'false' || raw === false) {
    return false;
  }
  return undefined;
}
